import React from 'react';
import { Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { getListOfPeriods, getPeriodName, getRebateUnitSuffix, makePeriod } from '../../utils/rebatePeriods';
import { NumericFormat } from 'react-number-format';

// Seperate Periods into sub-period rows based on period type
const buildPeriodRows = (startDate, endDate, period) => {
    const calculatedPeriods = getListOfPeriods(startDate, endDate, period);

    return calculatedPeriods.map((calculatedPeriod, index) => makePeriod({
        name: getPeriodName(period, index),
        startDate: calculatedPeriod.startDate,
        endDate: calculatedPeriod.endDate,
    }));
};

// Rows for when period type is Custom or Unknown
const getManualPeriodRows = (rebate) => rebate.periods?.length
    ? rebate.periods
    : [makePeriod({ name: 'Custom Period 1', startDate: rebate.startDate ?? '', endDate: rebate.endDate ?? '' })];

const getTiers = (periodRow) =>
    Array.isArray(periodRow.tiers) && periodRow.tiers.length
        ? periodRow.tiers
        : [{ threshold: '', rebateValue: '' }];

function PeriodCard({ rebate, rebateUnit, onChange }) {

    const requiresManualEntry = ['custom', 'unknown'].includes(rebate.periodType);
    const periodRows = requiresManualEntry
        ? getManualPeriodRows(rebate)
        : (rebate.periods?.length? rebate.periods : buildPeriodRows(rebate.startDate, rebate.endDate, rebate.periodType));

    const updatePeriod = (periodIndex, updater) => {
        onChange({
            ...rebate,
            periods: periodRows.map((periodRow, rowIndex) => {
                if (rowIndex !== periodIndex) {
                    return periodRow;
                }

                const updatedPeriod = updater(periodRow);
                const tiers = getTiers(updatedPeriod);

                return {
                    ...updatedPeriod,
                    tiers,
                    threshold: tiers[0]?.threshold ?? '',
                    rebateValue: tiers[0]?.rebateValue ?? '',
                };
            }), 
        });
    };

    const handleTierValueChange = (periodIndex, tierIndex, field, value) => {
        updatePeriod(periodIndex, (periodRow) => {
            const tiers = getTiers(periodRow).map((tier, index) => (
                index === tierIndex ? { ...tier, [field]: value } : tier
            ));

            return { ...periodRow, tiers };
        });
    };

    const handlePeriodHeaderChange = (periodIndex, field, value) => {
        updatePeriod(periodIndex, (periodRow) => ({
            ...periodRow,
            [field]: value,
        }));
    };

    const handleAddTier = (periodIndex) => {
        updatePeriod(periodIndex, (periodRow) => ({
            ...periodRow,
            tiers: [
                ...getTiers(periodRow),
                { threshold: '', rebateValue: '' },
            ],
        }));
    };

    const handleRemoveTier = (periodIndex) => {
        updatePeriod(periodIndex, (periodRow) => {
            const tiers = getTiers(periodRow);

            if (tiers.length <= 1) {
                return periodRow;
            }

            return {
                ...periodRow,
                tiers: tiers.slice(0, -1),
            };
        });
    };

    return (
        <Paper sx={{ borderRadius: 2 }}>
            <Stack spacing={1} px={2.5} py={1.75}>
                {periodRows.map((periodRow, index) => (
                    <Stack key={`${periodRow.startDate}-${periodRow.endDate}-${index}`} spacing={0.75}>
                        <Paper elevation={0} sx={{ borderRadius: 2, p: 2, backgroundColor: 'background.neutral' }}>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                {requiresManualEntry ? (
                                    <>
                                        <TextField
                                            value={periodRow.name ?? ''}
                                            onChange={(e) => handlePeriodHeaderChange(index, 'name', e.target.value)}
                                            size='small'
                                            variant='standard'
                                            sx={{ minWidth: 160 }}
                                        />
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <DetailField
                                                label='Start Date'
                                                value={periodRow.startDate ?? ''}
                                                onChange={(e) => handlePeriodHeaderChange(index, 'startDate', e.target.value)}
                                                type='date'
                                            />
                                            <DetailField
                                                label='End Date'
                                                value={periodRow.endDate ?? ''}
                                                onChange={(e) => handlePeriodHeaderChange(index, 'endDate', e.target.value)}
                                                type='date'
                                            />
                                        </Stack>
                                    </>
                                ) : (
                                    <>
                                        <Typography sx={{ fontSize: '13px', color: 'subtext.main', fontWeight: 600 }}>
                                            {periodRow.name ?? ''}
                                        </Typography>
                                        <Stack direction='row' alignItems='center'>
                                            <Typography sx={{ fontSize: '13px', color: 'subtext.main', fontWeight: 600 }}>
                                                (
                                            </Typography>
                                            <Typography sx={{ fontSize: '13px', color: 'subtext.main', fontWeight: 600 }}>
                                                {periodRow.startDate ?? ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: '13px', color: 'subtext.main', fontWeight: 600 }}>
                                                —
                                            </Typography>
                                            <Typography sx={{ fontSize: '13px', color: 'subtext.main', fontWeight: 600 }}>
                                                {periodRow.endDate ?? ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: '13px', color: 'subtext.main', fontWeight: 600 }}>
                                                )
                                            </Typography>
                                        </Stack>
                                    </>
                                )}
                            </Stack>
                        </Paper>

                        <Paper elevation={0} sx={{ borderRadius: 2, p: 1.5, backgroundColor: 'background.paper' }}>
                            <Stack direction='row' spacing={1}>
                                <Typography sx={{ flex: 1, fontSize: '12px', color: 'subtext.main', fontWeight: 600 }}>
                                    Threshold
                                </Typography>
                                <Typography sx={{ flex: 1, fontSize: '12px', color: 'subtext.main', fontWeight: 600 }}>
                                    {rebateUnit}
                                </Typography>
                            </Stack>

                            <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                                {getTiers(periodRow).map((tier, tierIndex) => (
                                    <Stack key={`${index}-tier-${tierIndex}`} direction='row' spacing={1}>
                                        <NumericFormat
                                            customInput={TextField}
                                            value={tier.threshold === '' || tier.threshold == null ? '' : Number(tier.threshold) / 100}
                                            thousandSeparator
                                            decimalScale={2}
                                            fixedDecimalScale={false}
                                            allowNegative={false}
                                            prefix="$"
                                            size="small"
                                            fullWidth
                                            onValueChange={({ floatValue }) => {
                                                handleTierValueChange(
                                                    index,
                                                    tierIndex,
                                                    'threshold',
                                                    floatValue == null ? '' : Math.round(floatValue * 100)
                                                );
                                            }}
                                        />
                                        <TextField
                                            value={tier.rebateValue ?? ''}
                                            onChange={(e) => handleTierValueChange(index, tierIndex, 'rebateValue', e.target.value)}
                                            size='small'
                                            fullWidth
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        {getRebateUnitSuffix(rebateUnit)}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Stack>
                                ))}
                            </Stack>

                            <Stack direction='row' spacing={2} sx={{ mt: 1 }}>
                                <Button
                                    onClick={() => handleAddTier(index)}
                                    sx={{
                                        p: 0,
                                        minWidth: 0,
                                        fontSize: '12px',
                                        color: 'blue.main',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        justifyContent: 'flex-start',
                                        width: 'fit-content',
                                    }}
                                >
                                    Add Tier
                                </Button>

                                <Button
                                    onClick={() => handleRemoveTier(index)}
                                    disabled={getTiers(periodRow).length <= 1}
                                    sx={{
                                        p: 0,
                                        minWidth: 0,
                                        fontSize: '12px',
                                        color: getTiers(periodRow).length > 1 ? 'error.main' : 'subtext.main',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        justifyContent: 'flex-start',
                                        opacity: getTiers(periodRow).length > 1 ? 1 : 0.6,
                                        width: 'fit-content',
                                    }}
                                >
                                    Remove Tier
                                </Button>
                            </Stack>
                        </Paper>
                    </Stack>
                ))}
            </Stack>
        </Paper>
    );
}

/**
 * 
 * This function is copied from EditContracts file and will need to be refactored to be used across files 
 * 
 */
function DetailField({ label, value, onChange, readOnly = false, type = 'text' }) {
    return (
        <Stack spacing={0.75} flex={1}>
            <Typography sx={{ fontSize: '13px', color: 'subtext.main' }}>
                {label}
            </Typography>
            <TextField
                type={type}
                value={value}
                onChange={onChange}
                fullWidth
                variant='outlined'
                size='small'
                InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                InputProps={readOnly ? { readOnly: true } : undefined}
                disabled={readOnly}
                sx={readOnly ? {
                    '& .MuiInputBase-root': {
                        backgroundColor: 'action.hover',
                    },
                } : undefined}
            />
        </Stack>
    );
}

export default PeriodCard;